/**
 * placeNormalizer.js — single canonical Place shape for the public site.
 *
 * The place data currently lives in three different schemas:
 *  - v1 maptodo CF:  title, string lat/lng, work_times, contact{phone,address,instagram},
 *                    menu_highlights, imgUris/cardImgUri (often local asset keys), images{cover,gallery}
 *  - M2B Prisma API: workTimes/menuHighlights (Json), Float lat/lng, imageUrl
 *  - v05:            name, short_description, why, contact.map.{latitude,longitude}
 *
 * normalizePlace() maps any of them into one unified shape so every page
 * (list, map, detail) renders the same data regardless of backend.
 */

import { ARCHIVED_TAG, SECRET_TAG, STAGE_TAG } from './placeFlags';
import {
  AMENITY_IDS,
  GOOD_FOR_IDS,
  filterAllowedIds,
  isHttpUrl,
} from './placeDetails';

const DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'holidays',
];

/** Only keep image refs the web can actually render (MapTODO also ships local asset keys). */
function isRenderableImageUrl(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  return /^https?:\/\//i.test(v) || v.startsWith('data:') || v.startsWith('/');
}

function toFiniteNumber(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function normalizeWorkTimes(raw) {
  const source = raw?.work_times || raw?.workTimes;
  if (!source || typeof source !== 'object') return null;

  const entries = [];
  const seen = new Set();
  const pushDay = (day) => {
    const times = source[day];
    if (!times || typeof times !== 'object') return;
    seen.add(day);
    entries.push({
      day,
      open: times.open != null ? String(times.open) : '',
      close: times.close != null ? String(times.close) : '',
    });
  };
  DAY_ORDER.forEach(pushDay);
  // Preserve any unexpected extra keys at the end
  Object.keys(source).forEach((day) => {
    if (!seen.has(day)) pushDay(day);
  });

  return entries.length > 0 ? entries : null;
}

function normalizeContact(raw) {
  const source = raw?.contact;
  if (!source || typeof source !== 'object') return null;

  const contact = {
    phone: typeof source.phone === 'string' ? source.phone.trim() : '',
    address: typeof source.address === 'string' ? source.address.trim() : '',
    instagram: typeof source.instagram === 'string' ? source.instagram.trim() : '',
    website: typeof source.website === 'string' ? source.website.trim() : '',
    email: typeof source.email === 'string' ? source.email.trim() : '',
    tiktok: typeof source.tiktok === 'string' ? source.tiktok.trim() : '',
    facebook: typeof source.facebook === 'string' ? source.facebook.trim() : '',
  };

  return contact.phone
    || contact.address
    || contact.instagram
    || contact.website
    || contact.email
    || contact.tiktok
    || contact.facebook
    ? contact
    : null;
}

function normalizeDetails(raw) {
  const source = raw?.details;
  if (!source || typeof source !== 'object') return null;

  const details = {};
  const priceRange = Number.parseInt(source.priceRange, 10);
  if (priceRange >= 1 && priceRange <= 4) details.priceRange = priceRange;

  if (typeof source.neighborhood === 'string') {
    const neighborhood = source.neighborhood.trim().slice(0, 40);
    if (neighborhood) details.neighborhood = neighborhood;
  }

  if (typeof source.vibe === 'string') {
    const vibe = source.vibe.trim().slice(0, 60);
    if (vibe) details.vibe = vibe;
  }

  const since = Number.parseInt(source.since, 10);
  const currentYear = new Date().getFullYear();
  if (since >= 1800 && since <= currentYear) details.since = since;

  if (isHttpUrl(source.reservationUrl)) {
    details.reservationUrl = source.reservationUrl.trim();
  }
  if (isHttpUrl(source.menuUrl)) {
    details.menuUrl = source.menuUrl.trim();
  }

  const amenities = filterAllowedIds(source.amenities, new Set(AMENITY_IDS));
  if (amenities.length) details.amenities = amenities;

  const goodFor = filterAllowedIds(source.goodFor, new Set(GOOD_FOR_IDS));
  if (goodFor.length) details.goodFor = goodFor;

  return Object.keys(details).length ? details : null;
}

function parseMenuItemString(str) {
  if (typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (!trimmed) return null;

  const dashMatch = trimmed.match(/^(.*?)\s*[\u2014\u2013—–-]\s*(.*)$/);
  if (dashMatch) {
    const name = dashMatch[1].trim();
    const price = dashMatch[2].trim();
    if (name && price) return { name, price, description: '' };
  }

  const colonMatch = trimmed.match(/^(.*?)\s*:\s*(.*)$/);
  if (colonMatch) {
    const name = colonMatch[1].trim();
    const price = colonMatch[2].trim();
    if (name && price) return { name, price, description: '' };
  }

  return { name: trimmed, price: '', description: '' };
}

function normalizeMenuSection(items) {
  if (!items) return [];

  let rawList = [];
  if (typeof items === 'string') {
    rawList = items.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  } else if (Array.isArray(items)) {
    for (const item of items) {
      if (typeof item === 'string') {
        const sub = item.split(/[\n]+/).map((s) => s.trim()).filter(Boolean);
        rawList.push(...sub);
      } else if (item && typeof item === 'object') {
        rawList.push(item);
      }
    }
  }

  const result = [];
  for (const rawItem of rawList) {
    if (typeof rawItem === 'string') {
      const parsed = parseMenuItemString(rawItem);
      if (parsed) result.push(parsed);
    } else if (rawItem && typeof rawItem === 'object') {
      let name = String(rawItem.name || '').trim();
      let price = rawItem.price != null ? String(rawItem.price).trim() : '';
      const description = typeof rawItem.description === 'string' ? rawItem.description.trim() : '';

      if (name && !price) {
        const parsed = parseMenuItemString(name);
        if (parsed && parsed.price) {
          name = parsed.name;
          price = parsed.price;
        }
      }

      if (name) {
        result.push({ name, price, description });
      }
    }
  }

  return result;
}

function normalizeMenuHighlights(raw) {
  const source = raw?.menu_highlights || raw?.menuHighlights;
  if (!source) return null;

  if (Array.isArray(source) || typeof source === 'string') {
    const items = normalizeMenuSection(source);
    return items.length ? { drinks: [], food: [], other: items } : null;
  }
  if (typeof source !== 'object') return null;

  const knownKeys = new Set(['drinks', 'food', 'other']);
  const menu = {
    drinks: normalizeMenuSection(source.drinks),
    food: normalizeMenuSection(source.food),
    other: normalizeMenuSection(source.other),
  };

  Object.keys(source).forEach((key) => {
    if (!knownKeys.has(key)) {
      const extraItems = normalizeMenuSection(source[key]);
      if (extraItems.length > 0) {
        menu.other = [...menu.other, ...extraItems];
      }
    }
  });

  return menu.drinks.length || menu.food.length || menu.other.length ? menu : null;
}

function collectImages(raw) {
  const thumb = isRenderableImageUrl(raw?.cardThumbUri) ? raw.cardThumbUri.trim() : '';
  const candidates = [
    raw?.imageUrl,
    raw?.cardImgUri,
    ...(Array.isArray(raw?.imgUris) ? raw.imgUris : []),
    raw?.images?.cover,
    ...(Array.isArray(raw?.images?.gallery) ? raw.images.gallery : []),
    raw?.markerImgUri,
  ];

  const urls = [];
  for (const candidate of candidates) {
    if (!isRenderableImageUrl(candidate)) continue;
    const url = candidate.trim();
    if (thumb && url === thumb) continue;
    if (!urls.includes(url)) urls.push(url);
  }
  return urls;
}

/** List-card photo. Falls back to the full cover when no thumb has been stored yet. */
export function placeCardImageSrc(place) {
  if (!place || typeof place !== 'object') return '';
  const candidates = [
    place.cardImage,
    place.cardThumbUri,
    place.coverImage,
    place.cardImgUri,
    Array.isArray(place.imgUris) ? place.imgUris[0] : '',
    place.imageUrl,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return '';
}

/** Open-place hero. Full cover first; the card thumb only until that cover exists. */
export function placeHeroImageSrc(place) {
  if (!place || typeof place !== 'object') return '';
  const images = Array.isArray(place.images) ? place.images : [];
  const full = place.coverImage || images[0] || '';
  if (typeof full === 'string' && full.trim()) return full.trim();
  const thumb = place.cardImage || place.cardThumbUri || '';
  return typeof thumb === 'string' ? thumb.trim() : '';
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => typeof item === 'string' && item.trim()).map((s) => s.trim());
}

/**
 * @param {Record<string, any>} raw place in any known backend schema
 * @returns {object|null} canonical place
 */
export function normalizePlace(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const id = raw.id ?? raw._id;
  const lat = toFiniteNumber(raw.lat) ?? toFiniteNumber(raw?.contact?.map?.latitude);
  const lng = toFiniteNumber(raw.lng) ?? toFiniteNumber(raw?.contact?.map?.longitude);
  const images = collectImages(raw);

  const tags = normalizeStringArray(raw.tags);
  const flagsFromRaw = raw.flags && typeof raw.flags === 'object' ? { ...raw.flags } : {};
  const flags = {
    ...flagsFromRaw,
    ...(tags.includes(ARCHIVED_TAG) ? { archived: true } : {}),
    ...(tags.includes(SECRET_TAG) ? { secret: true } : {}),
    ...(tags.includes(STAGE_TAG) ? { stage: true } : {}),
  };

  return {
    id: id != null ? String(id) : '',
    title: String(raw.title || raw.name || '').trim(),
    lat,
    lng,
    hasCoordinates: lat != null && lng != null,
    description: String(raw.description || raw.why || '').trim(),
    shortDescription: String(raw.shortDescription || raw.short_description || '').trim(),
    mainCategory: typeof raw.mainCategory === 'string' ? raw.mainCategory.trim() : '',
    categories: normalizeStringArray(raw.categories),
    personalityRaitingProperty:
      typeof raw.personalityRaitingProperty === 'string'
        ? raw.personalityRaitingProperty.trim()
        : '',
    tags,
    images,
    coverImage: images[0] || '',
    cardImage: isRenderableImageUrl(raw?.cardThumbUri) ? raw.cardThumbUri.trim() : '',
    workTimes: normalizeWorkTimes(raw),
    contact: normalizeContact(raw),
    details: normalizeDetails(raw),
    menuHighlights: normalizeMenuHighlights(raw),
    isPartner: Boolean(raw.isPartner),
    ownerUid: typeof raw.ownerUid === 'string' ? raw.ownerUid : null,
    defaultRating: toFiniteNumber(raw.defaultRating),
    minAway: toFiniteNumber(raw.minAway),
    flags,
  };
}

/** Normalize a backend list response, dropping entries without an id or title. */
export function normalizePlaceList(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList
    .map(normalizePlace)
    .filter((place) => place && place.id && place.title);
}
